import React, { useCallback } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useCombinedConversations, UnifiedConversation } from '@/hooks/residents/useCombinedConversations';
import type { MessagesStackParamList } from '@/navigation/residents/mobile/MessagesStackNavigator';
import type { StackNavigationProp } from '@react-navigation/stack';
import { CHAT_MOBILE_COLORS } from '@/constants/residents/mobile/chatColors';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';

// Layout components
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';

// Chat components
import {
  ChatListHeader,
  ChatConversationCard,
  ChatEmptyState,
  SortOption,
} from '@/components/residents/chat/mobile';

type MessagesNav = StackNavigationProp<MessagesStackParamList, 'ConversationList'>;

/**
 * Mobile Conversation List Screen
 * Displays property chat conversations (admin-resident messages)
 */
export const ConversationListScreen: React.FC = () => {
  const navigation = useNavigation<MessagesNav>();
  
  // Use conversations hook (property chat only)
  const {
    conversations,
    isLoading,
    refreshing,
    isEmpty,
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
    refetchAll,
    markAsReadLocally,
  } = useCombinedConversations();

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    await refetchAll();
  }, [refetchAll]);

  // Handle sort change - cast to match hook's MessageSortOption type
  const handleSortChange = useCallback((option: SortOption) => {
    setSortOption(option as any);
  }, [setSortOption]);

  // Handle compose press
  const handleComposePress = useCallback(() => {
    navigation.navigate('NewConversation');
  }, [navigation]);

  // Handle conversation press - navigate to property chat conversation
  const handleConversationPress = useCallback((id: number | string) => {
    // Find the conversation to get its details
    const conversation = conversations.find((c: UnifiedConversation) => c.id === id);
    
    if (!conversation) return;
    
    // Optimistically mark as read locally for instant UI feedback
    if (conversation.hasUnread) {
      markAsReadLocally(conversation.originalId);
    }
    
    // Navigate to property chat conversation
    navigation.navigate('Conversation', {
      conversationId: conversation.originalId,
      subject: conversation.title,
    });
  }, [conversations, navigation, markAsReadLocally]);

  // Render content
  const renderContent = () => {
    if (isLoading && !refreshing) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
        </View>
      );
    }

    if (isEmpty) {
      return (
        <View style={styles.emptyContainer}>
          {/* Header with search, compose - hide sort row in empty state per Figma */}
          <ChatListHeader
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onComposePress={handleComposePress}
            sortOption={sortOption as SortOption}
            onSortChange={handleSortChange}
            hideSortRow
          />
          <ChatEmptyState type="noMessages" />
        </View>
      );
    }

    return (
      <View style={styles.listWrapper}>
        {/* Header with search, compose, sort */}
        <ChatListHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onComposePress={handleComposePress}
          sortOption={sortOption as SortOption}
          onSortChange={handleSortChange}
        />
        {/* Conversation list */}
        <View style={styles.listContainer}>
          {conversations.map((item: UnifiedConversation) => (
            <ChatConversationCard
              key={item.id}
              id={item.id}
              subject={item.title}
              preview={item.preview}
              date={item.date}
              isGroup={item.isGroup}
              recipientCount={item.recipientCount}
              hasUnread={item.hasUnread}
              onPress={handleConversationPress}
            />
          ))}
        </View>
      </View>
    );
  };

  return (
    <BaseResidentMobileScreen screenName="Messages" darkBackground>
      <MobileScreenLayout
        backgroundColor={CHAT_MOBILE_COLORS.background}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        refreshTintColor={CHAT_MOBILE_COLORS.textPrimary}
        lightStatusBar={true}
        contentGap={0}
        contentPaddingBottom={0}
      >
        {renderContent()}
      </MobileScreenLayout>
    </BaseResidentMobileScreen>
  );
};

const styles = StyleSheet.create({
  // Container with cardBackground and rounded corners per Figma (used for both empty and list states)
  emptyContainer: {
    flex: 1,
    backgroundColor: CHAT_MOBILE_COLORS.cardBackground,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
  },

  // List wrapper with same styling as empty container
  listWrapper: {
    flex: 1,
    backgroundColor: CHAT_MOBILE_COLORS.cardBackground,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
  },

  // Loading
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    backgroundColor: CHAT_MOBILE_COLORS.cardBackground,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },

  // List
  listContainer: {
    gap: 12,
  },
});

export default ConversationListScreen;
